import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { useSelector } from "react-redux";
import { ArrowDown, ArrowUp } from "lucide-react";
import ExportIcon from "../../icons/ExportIcon";
import ChartCard from "../components/ChartCard";
import LineAreaChart from "../components/charts/LineAreaChart";
import InputInlineWidget from "../components/InputInlineWidget";
import { colors as defaultColors } from "../../board/components/TaskDetail/ColorPicker";
import { handleDataLineChart } from "../DataUtils";
import { useAggregatedData, useAggregatedDataCompare } from "../hooks/useReportData";
import ChartLoading from "../components/ChartLoading";

const ChartLineWidget = ({
  projectId,
  widget,
  className,
  onClickUp,
  onClickDown,
  onClickExport,
  onClickSetting,
  onClickDelete,
  onUpdateTitle,
  onUpdateSubtitle,
}) => {
  const { t } = useTranslation();

  const ruleReducer = useSelector((state: any) => state.ruleReducer);

  const objectType = widget.data?.objectType || "SALE";
  const title = widget.data?.title;
  const subtitle = widget.data?.subtitle;
  const viewBy = widget.data?.nameKey || "bucketId";
  const segmentBy = widget.data?.segmentBy || "None";
  const measureBy = widget.data?.valueKey || "dealCount";
  const dateUnit = widget.data?.dateUnit || "day";
  const comparisonBy = widget.data?.comparisonBy || "none";
  const filters = widget.data?.filters || {};
  const colors = widget.data?.colors || [];
  const lineColors = colors.length > 0 ? colors : defaultColors;
  const groups = ruleReducer.get(viewBy) || fromJS([]);

  const shouldUseComparison = comparisonBy !== "none";

  const { data: dataAggregated, isLoading: isLoadingAggregated } = useAggregatedData(
    projectId,
    viewBy,
    segmentBy,
    dateUnit,
    filters,
    objectType,
    !shouldUseComparison
  );

  const { data: dataCompare, isLoading: isLoadingCompare } = useAggregatedDataCompare(
    projectId,
    viewBy,
    segmentBy,
    dateUnit,
    filters,
    objectType,
    comparisonBy,
    shouldUseComparison,
    false // useReduxStore = false for widgets
  );

  const { currentData, previousData } = dataCompare || {};

  return (
    <ChartCard
      title={
        <InputInlineWidget
          placeholder={t("e.g Deal Performance")}
          value={title}
          className="text-sm sm:text-sm/6 md:text-base font-medium text-gray-600 placeholder:text-gray-400"
          onFinish={onUpdateTitle}
        />
      }
      subtitle={
        subtitle ? (
          <InputInlineWidget
            placeholder={t("e.g Last year")}
            value={subtitle}
            className="text-sm text-gray-600 placeholder:text-gray-400"
            onFinish={onUpdateSubtitle}
          />
        ) : null
      }
      className={className}
      isLoading={shouldUseComparison ? isLoadingCompare : isLoadingAggregated}
      actions={[
        {
          label: t("Up"),
          content: <ArrowUp className="w-4 h-4 text-gray-600" />,
          onClick: (event) => onClickUp(event),
        },
        {
          label: t("Down"),
          content: <ArrowDown className="w-4 h-4 text-gray-600" />,
          onClick: (event) => onClickDown(event),
        },
        {
          label: t("Export"),
          content: <ExportIcon />,
          onClick: () => onClickExport(filters),
        },
      ]}
      onSetting={onClickSetting}
      onDelete={onClickDelete}
    >
      {(shouldUseComparison ? dataCompare : dataAggregated) &&
      !(shouldUseComparison ? isLoadingCompare : isLoadingAggregated) ? (
        <LineAreaChart
          data={
            shouldUseComparison
              ? handleDataLineChart(
                  currentData || [],
                  previousData || [],
                  viewBy,
                  measureBy,
                  dateUnit,
                  groups,
                  comparisonBy
                )
              : handleDataLineChart(
                  dataAggregated,
                  [],
                  viewBy,
                  measureBy,
                  dateUnit,
                  groups,
                  comparisonBy
                )
          }
          lineColors={lineColors}
          mode="area"
        />
      ) : (
        <ChartLoading />
      )}
    </ChartCard>
  );
};

export default ChartLineWidget;
